/**
 * Daily Goal Progress - Feature L: Gamification
 * Shows revenue goal progress with motivating visuals
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { TrendingUp, Target, Zap } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function DailyGoalProgress({ sessionToken }) {
  const [goal, setGoal] = useState(null);

  useEffect(() => {
    if (!sessionToken) return;
    
    const loadGoal = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/kitchen/goals/daily`, {
          headers: { 'Authorization': `Bearer ${sessionToken}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setGoal(data);
        }
      } catch (error) {
        console.error('Goal load error:', error);
      }
    };
    
    loadGoal();
    const interval = setInterval(loadGoal, 60000); // Every minute
    return () => clearInterval(interval);
  }, [sessionToken]);

  if (!goal) return null;

  const progressColor = goal.progress_percentage >= 100 ? 'emerald' : 
                       goal.progress_percentage >= 75 ? 'teal' :
                       goal.progress_percentage >= 50 ? 'blue' : 'slate';

  return (
    <Card className="bg-gradient-to-br from-[#111C32] to-[#0E1628] border-slate-700/50 overflow-hidden">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-teal-500/20 flex items-center justify-center">
              <Target className="w-5 h-5 text-teal-400" />
            </div>
            <div>
              <div className="text-sm text-slate-400">Tagesziel</div>
              <div className="text-xl font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {goal.daily_goal.toFixed(0)}€
              </div>
            </div>
          </div>
          
          <div className="text-right">
            <div className="text-sm text-slate-400">Aktuell</div>
            <div className={`text-2xl font-bold text-${progressColor}-400`} style={{ fontFamily: "'Fira Code', monospace" }}>
              {goal.current_revenue.toFixed(2)}€
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="relative h-3 bg-slate-800/50 rounded-full overflow-hidden mb-2">
          <div 
            className={`absolute inset-y-0 left-0 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full transition-all duration-500`}
            style={{ width: `${Math.min(100, goal.progress_percentage)}%` }}
          >
            {goal.is_goal_reached && (
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/30 to-teal-400/30 animate-pulse" />
            )}
          </div>
        </div>

        <div className="flex items-center justify-between text-xs">
          <span className="text-slate-400">{goal.progress_percentage.toFixed(1)}% erreicht</span>
          {goal.is_goal_reached ? (
            <span className="text-emerald-400 font-semibold flex items-center gap-1">
              <Zap className="w-3 h-3" />
              Ziel erreicht!
            </span>
          ) : (
            <span className="text-slate-400">Noch {goal.remaining.toFixed(2)}€</span>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 mt-4 pt-3 border-t border-slate-700/50">
          <div>
            <div className="text-xs text-slate-400">Bestellungen</div>
            <div className="text-lg font-bold text-white">{goal.order_count}</div>
          </div>
          <div>
            <div className="text-xs text-slate-400">Ø Bestellung</div>
            <div className="text-lg font-bold text-teal-400">{goal.avg_order.toFixed(2)}€</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
